// 오버레이 타입별 기본값 SSOT — 편집기(ReactionEditor/*)와 렌더러(components-model/overlay/OverlayItem)가 공유.
//  overlayType: 'text' | 'caption' | 'lottie' | 'sticker'
//
//  transform 포맷 (타입별로 다름):
//   - 박스형(lottie/sticker): { x, y, w, h } — x,y = "좌상단" %, w/h = 스테이지 대비 % (자유 비율)
//     · 구 포맷 { x, y(센터), size(정사각) }은 normalizeTransform이 좌상단+w/h로 정확 변환 (하위호환)
//   - 자막(caption): { x, y, w, h, size } — 배경 밴드가 1급 박스(좌상단+자유 비율), size = 폰트 크기.
//     텍스트는 style.hAlign/vAlign(기본 left/top)으로 박스 안에 배치.
//     · 구 포맷 { x,y(센터), size, 폭90 고정 }은 동등한 박스로 변환 (하위호환)
//   - 텍스트(text): { x, y, w, h, size } — 표시 영역 박스(좌상단+자유 비율) + size=폰트. 좁으면 wrap.
//     · 구 포맷 { x,y(센터), size, auto 폭 }은 "레거시 센터 모드"로 그대로 렌더(무회귀) —
//       에디터 로드 시에만 텍스트 길이 기반으로 동등 박스로 변환(serialize.legacyTextToBox)

export const OVERLAY_DEFAULT_MS = { text: 2200, caption: 3200, lottie: 4000, sticker: 8000 };

export function isBoxOverlay(overlayType) {
  return overlayType === "lottie" || overlayType === "sticker";
}

// 밴드형 = 좌상단+w/h 박스로 배치/리사이즈되는 타입 (자막 포함 — 자막은 size(폰트)를 추가로 가짐)
export function isBandOverlay(overlayType) {
  return isBoxOverlay(overlayType) || overlayType === "caption";
}

export function defaultTransform(overlayType) {
  if (overlayType === "caption") return { x: 0, y: 75, w: 100, h: 25, size: 8 }; // 방송박스(400px) 기준 400×100, bottom-left
  if (overlayType === "text") return { x: 20, y: 28, w: 60, h: 24, size: 20 };
  if (overlayType === "lottie") return { x: 0, y: 0, w: 100, h: 100 };
  return { x: 25, y: 25, w: 50, h: 50 }; // sticker
}

// 어떤 포맷이 오든 렌더/편집용 표준 포맷으로 정규화.
//  박스형: 항상 { x, y, w, h } (레거시 센터+size → 좌상단+정사각 정확 변환 — 시각 결과 동일)
//  텍스트형: 항상 { x, y, size }
export function normalizeTransform(overlayType, t) {
  const d = defaultTransform(overlayType);
  if (!t) return { ...d };
  if (overlayType === "caption") {
    if (t.w != null || t.h != null) {
      return {
        x: t.x != null ? t.x : d.x,
        y: t.y != null ? t.y : d.y,
        w: t.w != null ? t.w : d.w,
        h: t.h != null ? t.h : d.h,
        size: t.size != null ? t.size : d.size,
      };
    }
    // 레거시: x,y=센터, 폭 90 고정, 밴드 높이 ≈ 폰트*1.1 (기존 시각과 근사)
    const size = t.size != null ? t.size : d.size;
    const h = Math.round(size * 1.1);
    return { x: (t.x != null ? t.x : 50) - 45, y: (t.y != null ? t.y : 88) - h / 2, w: 90, h, size };
  }
  if (overlayType === "text") {
    if (t.w != null || t.h != null) {
      return {
        x: t.x != null ? t.x : d.x,
        y: t.y != null ? t.y : d.y,
        w: t.w != null ? t.w : d.w,
        h: t.h != null ? t.h : d.h,
        size: t.size != null ? t.size : d.size,
      };
    }
    // 레거시 센터 모드 (w 없음) — 런타임은 예전 그대로 렌더, 변환은 에디터 로드에서
    return { x: t.x != null ? t.x : 50, y: t.y != null ? t.y : 40, size: t.size != null ? t.size : d.size };
  }
  if (t.w != null || t.h != null) {
    const w = t.w != null ? t.w : t.h;
    const h = t.h != null ? t.h : t.w;
    return { x: t.x != null ? t.x : d.x, y: t.y != null ? t.y : d.y, w, h };
  }
  // 레거시: x,y=센터, size=정사각 한 변
  const s = t.size != null ? t.size : d.w;
  const cx = t.x != null ? t.x : d.x + d.w / 2;
  const cy = t.y != null ? t.y : d.y + d.h / 2;
  return { x: cx - s / 2, y: cy - s / 2, w: s, h: s };
}

export function defaultDuration(overlayType) {
  return OVERLAY_DEFAULT_MS[overlayType] || 2200;
}
